/**
 * Thin REST client for the Spring Boot backend. Every call attaches an Entra access
 * token (see integrations/auth/entra.ts) as a Bearer header. This is what replaces the
 * Supabase JS client across the app.
 *
 * In production the backend serves the SPA, so requests are same-origin ('' base). In
 * dev, the webpack dev server proxies /api to the backend (see webpack.config.js).
 */
import { acquireToken } from '@/integrations/auth/entra';

const BASE = (process.env.APP_API_BASE_URL as string | undefined) ?? '';

async function authHeader(): Promise<Record<string, string>> {
  const token = await acquireToken();
  return { Authorization: `Bearer ${token}` };
}

/** Error carrying the HTTP status, so callers can branch on it (e.g. 409 = duplicate). */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function parse(res: Response) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { message?: string }));
    throw new ApiError(body?.message || `Request failed (${res.status})`, res.status);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

async function request(method: string, path: string, body?: unknown) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parse(res);
}

export const api = {
  get: (path: string) => request('GET', path),
  post: (path: string, body?: unknown) => request('POST', path, body),
  patch: (path: string, body?: unknown) => request('PATCH', path, body),
  del: (path: string) => request('DELETE', path),

  /** Multipart upload (the browser sets the Content-Type/boundary itself). */
  async upload(path: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { ...(await authHeader()) },
      body: form,
    });
    return parse(res);
  },

  /**
   * Fetch a protected binary (e.g. a receipt image) and return a blob object URL.
   * Remember to URL.revokeObjectURL(url) when done.
   */
  async objectUrl(path: string): Promise<string> {
    const res = await fetch(`${BASE}${path}`, { headers: { ...(await authHeader()) } });
    if (!res.ok) {
      throw new Error(`Failed to load file (${res.status})`);
    }
    return URL.createObjectURL(await res.blob());
  },

  /**
   * Like objectUrl, but also surfaces the filename the backend suggested via
   * Content-Disposition — for downloads where the server (not the browser tab) knows the
   * right name (e.g. it derived initials/dates the frontend doesn't have on hand).
   */
  async download(path: string): Promise<{ url: string; filename: string | null }> {
    const res = await fetch(`${BASE}${path}`, { headers: { ...(await authHeader()) } });
    if (!res.ok) {
      throw new Error(`Failed to load file (${res.status})`);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { url: URL.createObjectURL(await res.blob()), filename: match ? match[1] : null };
  },
};
